// =============================================================================
// client/src/lib/flux/importMapping.js | v1.0 | S81 | P9b W-3 mapping reducers
// =============================================================================
// The import form's column-mapping state, as pure functions.
//
// 🔴 THE MAPPING IS FIELD-KEYED: { field: headerIndex }. Not the other way
// round. The server is the authority on this shape at three separate points:
//   · autoMapColumns writes proposedMapping[field] = idx, so the preview's
//     proposed_mapping arrives field-keyed;
//   · validateMapping checks MAPPABLE_KEYS on the KEY and an integer header
//     index on the VALUE — an index-keyed map is refused "unknown mapping
//     key: 0";
//   · prepareImportRows tests `field in mapping`.
//
// These live outside the component because the inversion they fix shipped: the
// component read and wrote mapping[headerIndex] = field, which meant the
// dropdowns rendered empty against a perfectly good proposed_mapping, the
// Import button could never enable, and a submission that got through would be
// refused by the validator. A reducer that can be run by node --test against
// the REAL server validator cannot fail that way unnoticed again.
//
// The two invariants validateMapping enforces are held here BY CONSTRUCTION:
//   · no field mapped twice — object keys;
//   · no header index used twice — assign() releases any field already
//     pointing at that index before setting the new one.
// =============================================================================

/** The sentinel the <select> uses for "not mapped". */
export const SKIP = '__skip__';

/**
 * Which field is currently mapped to this header column?
 * @param {Record<string, number>} mapping field-keyed mapping
 * @param {number} headerIndex
 * @returns {string} the field name, or SKIP when the column is unmapped
 */
export function selectedFieldFor(mapping, headerIndex) {
  const found = Object.keys(mapping || {}).find((field) => mapping[field] === headerIndex);
  return found ?? SKIP;
}

/**
 * Apply a dropdown change for one header column.
 *
 * @param {Record<string, number>} mapping field-keyed mapping
 * @param {number} headerIndex the column the operator changed
 * @param {string} field the chosen field, or SKIP to unmap the column
 * @returns {Record<string, number>} a NEW mapping (never mutated in place)
 */
export function applyMapping(mapping, headerIndex, field) {
  const next = { ...(mapping || {}) };

  // Release whatever field currently owns this column. This runs on BOTH
  // branches: choosing — skip — unmaps the column, and choosing a field must
  // not leave the old field pointing at a column it no longer owns.
  for (const key of Object.keys(next)) {
    if (next[key] === headerIndex) delete next[key];
  }

  if (field === SKIP) return next;

  // A field can only be in one place, so re-selecting a field that already
  // sits on another column MOVES it rather than duplicating it.
  next[field] = headerIndex;
  return next;
}

/** `email` is the one required mapping — the server refuses without it. */
export function isEmailMapped(mapping) {
  return 'email' in (mapping || {});
}
